import { config } from "../config";

// Thin wrapper around fetch that:
//   - prefixes every URL with the configured API base, when a relative path
//     is passed in.
//   - sends cookies (the backend stores auth in HttpOnly cookies, and the
//     legacy fetch helpers in js/auth.js all used `credentials: "include"`).
//   - attempts a single silent /auth/refresh_token retry on 401, mirroring
//     the behaviour of the old `apiFetch` helper in main.js.
//
// Returns the raw Response so callers can decide whether to .json() / .text()
// or stream the body (e.g. for the upload endpoint).
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${config.apiUrl}${path}`;

  const doFetch = (): Promise<Response> =>
    fetch(url, {
      ...init,
      credentials: "include",
      headers: {
        // Only set Content-Type when there is a body and the caller didn't
        // already specify one. This avoids stomping on multipart uploads.
        ...(init.body && !(init.body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(init.headers || {}),
      },
    });

  let res = await doFetch();
  if (res.status !== 401 || path.includes("/auth/refresh_token")) {
    return res;
  }

  // Token expired — try once to refresh, then replay the request.
  try {
    const refreshRes = await fetch(`${config.apiUrl}/auth/refresh_token`, {
      method: "POST",
      credentials: "include",
    });
    if (refreshRes.ok) {
      res = await doFetch();
    }
  } catch {
    /* fall through with the original 401 */
  }
  return res;
}

export async function apiJson<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}
